import React, { useEffect, useRef, useState } from 'react';
import { Pipette } from 'lucide-react';
import useEyeDropper from 'use-eye-dropper';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  onClose: () => void;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, onClose }) => {
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [hexValue, setHexValue] = useState(color);
  const [isDragging, setIsDragging] = useState(false);
  const [isHueDragging, setIsHueDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const { open, isSupported } = useEyeDropper();
  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleResize = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addListener(handleResize);
    return () => mediaQuery.removeListener(handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (isDragging && saturationRef.current) {
        e.preventDefault();
        const rect = saturationRef.current.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
        
        setSaturation(x * 100);
        setLightness((1 - y) * 100);
        
        const newColor = hslToHex(hue, x * 100, (1 - y) * 100);
        setHexValue(newColor);
        onChange(newColor);
      }

      if (isHueDragging && hueRef.current) {
        e.preventDefault();
        const rect = hueRef.current.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const newHue = x * 360;
        
        setHue(newHue);
        const newColor = hslToHex(newHue, saturation, lightness);
        setHexValue(newColor);
        onChange(newColor);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      setIsHueDragging(false);
    };

    if (isDragging || isHueDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, isHueDragging, hue, saturation, lightness, onChange]);

  useEffect(() => {
    // Convert hex to HSL on initial load
    const r = parseInt(color.slice(1, 3), 16) / 255;
    const g = parseInt(color.slice(3, 5), 16) / 255;
    const b = parseInt(color.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    setHue(h * 360);
    setSaturation(s * 100);
    setLightness(l * 100);
  }, [color]);

  const handleStart = (type: 'saturation' | 'hue', e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ref = type === 'saturation' ? saturationRef : hueRef;
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const clientX = 'touches' in e 
      ? e.touches[0].clientX 
      : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e 
      ? e.touches[0].clientY 
      : (e as React.MouseEvent).clientY;

    if (type === 'saturation') {
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
      
      setSaturation(x * 100);
      setLightness((1 - y) * 100);
      
      const newColor = hslToHex(hue, x * 100, (1 - y) * 100);
      setHexValue(newColor);
      onChange(newColor);
      setIsDragging(true);
    } else {
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newHue = x * 360;
      
      setHue(newHue);
      const newColor = hslToHex(newHue, saturation, lightness);
      setHexValue(newColor);
      onChange(newColor);
      setIsHueDragging(true);
    }
  };

  const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toUpperCase();
    if (/^#[0-9A-F]{0,6}$/.test(value)) {
      setHexValue(value);
      if (value.length === 7) {
        onChange(value);
      }
    }
  };

  const handleEyeDropper = async () => {
    try {
      const { sRGBHex } = await open();
      const newColor = sRGBHex.toUpperCase();
      setHexValue(newColor);
      onChange(newColor);
    } catch (e) {
      console.log(e);
    }
  };

  const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
  };

  // Calculate position for the color picker
  useEffect(() => {
    if (pickerRef.current) {
      const rect = pickerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Center the picker in the viewport
      pickerRef.current.style.top = '50%';
      pickerRef.current.style.left = '50%';
      pickerRef.current.style.transform = 'translate(-50%, -50%)';
      
      // Ensure it doesn't go off-screen
      if (rect.bottom > viewportHeight) {
        const topOffset = Math.max(0, (viewportHeight - rect.height) / 2);
        pickerRef.current.style.top = `${topOffset}px`;
        pickerRef.current.style.transform = 'translateX(-50%)';
      }
      
      if (rect.right > viewportWidth) {
        const leftOffset = Math.max(0, (viewportWidth - rect.width) / 2);
        pickerRef.current.style.left = `${leftOffset}px`;
        pickerRef.current.style.transform = 'translateY(-50%)';
      }
    }
  }, []);

  return (
    <div 
      ref={pickerRef}
      className="fixed p-4 bg-white rounded-lg shadow-xl border border-gray-200 z-50 color-picker"
    >
      <div 
        ref={saturationRef}
        onMouseDown={(e) => handleStart('saturation', e)}
        onTouchStart={(e) => handleStart('saturation', e)}
        className="w-64 h-40 rounded-lg cursor-crosshair relative overflow-hidden touch-none"
        style={{
          backgroundColor: `hsl(${hue}, 100%, 50%)`
        }}
      >
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to right, #fff 0%, transparent 100%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, transparent 0%, #000 100%)'
        }} />
        <div
          className="absolute w-4 h-4 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 touch-none"
          style={{
            left: `${saturation}%`,
            top: `${100 - lightness}%`
          }}
        />
      </div>

      <div 
        ref={hueRef}
        onMouseDown={(e) => handleStart('hue', e)}
        onTouchStart={(e) => handleStart('hue', e)}
        className="h-4 rounded-lg mt-4 cursor-pointer relative touch-none"
        style={{
          background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)'
        }}
      >
        <div
          className="absolute w-4 h-4 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 top-1/2 touch-none"
          style={{ left: `${(hue / 360) * 100}%` }}
        />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input
          type="text"
          value={hexValue}
          onChange={handleHexChange}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          maxLength={7}
        />
        {!isMobile && isSupported() && (
          <button
            onClick={handleEyeDropper}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title="Pick color from screen"
          >
            <Pipette className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
};